import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { contentAPI } from '../services/api';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const [content, setContent] = useState('');
  const [lastUpdated, setLastUpdated] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const data = await contentAPI.getPrivacy();
      setContent(data.content);
      setLastUpdated(data.last_updated);
    } catch (error) {
      console.error('Error loading privacy policy:', error);
      setContent('Error loading Privacy Policy. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const renderContent = (text) => {
    // Simple markdown-like parsing
    return text.split('\n').map((line, index) => {
      if (line.startsWith('## ')) {
        return <h2 key={index} className="text-xl font-semibold text-white mb-3 mt-6">{line.replace('## ', '')}</h2>;
      } else if (line.startsWith('# ')) {
        return <h1 key={index} className="text-2xl font-bold text-white mb-4">{line.replace('# ', '')}</h1>;
      } else if (line.startsWith('- ')) {
        return <li key={index} className="ml-4">{line.replace('- ', '')}</li>;
      } else if (line.trim() === '') {
        return <br key={index} />;
      } else {
        // Bold text
        const parts = line.split('**');
        return (
          <p key={index} className="mb-2">
            {parts.map((part, i) => (
              i % 2 === 1 ? <strong key={i}>{part}</strong> : part
            ))}
          </p>
        );
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(to bottom, #0F1D2B, #17283A)' }}>
        <Loader2 className="w-8 h-8 text-wheely-orange-light animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(to bottom, #0F1D2B, #17283A)' }}>
      {/* Header */}
      <div className="border-b" style={{ background: 'linear-gradient(to right, #1E3448, #0F1D2B)', borderColor: 'rgba(225, 116, 49, 0.3)' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="text-wheely-orange-light hover:text-wheely-orange-light"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Card style={{ background: 'rgba(30, 58, 74, 0.8)', borderColor: 'rgba(225, 116, 49, 0.3)' }}>
          <CardHeader>
            <CardTitle className="text-3xl font-bold text-white">Privacy Policy</CardTitle>
            <p className="text-gray-400 mt-2">
              Last updated: {lastUpdated ? new Date(lastUpdated).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Loading...'}
            </p>
          </CardHeader>
          <CardContent className="text-gray-300 space-y-2">
            {renderContent(content)}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
